// =============================================================================
// La page d'aide « Comprendre l'étude » parle du compteur d'où l'on vient
// (contrat de l'interface v1.3, §9, N7, S0, S5, S6, S9 ; lot 4i, 19/10/2026).
//
// Ce que l'utilisateur doit vivre : venu d'une étude C5, il lit « Ce compteur
// (C5) » et rien des deux autres segments ; sans segment, la page générale.
// Au repos, « L'essentiel » est ouvert, « Ce qui change avec la nouvelle
// version » aussi jusqu'au 31/12/2026, tout le reste est replié derrière son
// titre. Fonctions pures : la page les appelle, les tests les figent.
// =============================================================================
import { FIN_DE_VIE_V1 } from "@modules/courtage/constants/optim-turpe-v2"
import type {
    SectionAide,
    SegmentAide,
} from "@modules/courtage/utils/optim-turpe-v2.aide.contenu"

export const SEGMENTS_AIDE: readonly SegmentAide[] = ["C5", "C4", "C2"]

/** Le paramètre « segment » de l'adresse, s'il est l'un des trois ; sinon `null` (page générale). */
export function segmentDeLAdresse(valeur: unknown): SegmentAide | null {
    return typeof valeur === "string" &&
        (SEGMENTS_AIDE as readonly string[]).includes(valeur)
        ? (valeur as SegmentAide)
        : null
}

/** Retire les questions réservées à un autre segment (`<details data-segments="…">`). */
export function questionsDuSegment(
    html: string,
    segment: SegmentAide | null
): string {
    if (!segment) return html
    return html.replace(
        /<details data-segments="([^"]*)">[\s\S]*?<\/details>\n?/g,
        (bloc, liste: string) =>
            liste.split(/\s+/).includes(segment) ? bloc : ""
    )
}

/**
 * Les sections à rendre pour un segment : celles d'un autre segment retirées
 * (« Les deux scénarios » absente en C5), la variante du segment à la place
 * du tableau à trois colonnes (« Ce compteur (C4) »), les questions d'un autre
 * segment masquées. Sans segment, toutes les sections, telles quelles.
 */
export function sectionsDuSegment(
    sections: readonly SectionAide[],
    segment: SegmentAide | null
): SectionAide[] {
    const gardees = sections.filter(
        (s) => !segment || !s.segments || s.segments.includes(segment)
    )
    const ancres = new Set(gardees.map((s) => s.id))
    return gardees.map((s) => {
        const variante = segment ? s.variantes?.[segment] : undefined
        const base = variante ? { ...s, ...variante } : { ...s }
        return {
            ...base,
            html: liensVersSectionsRendues(
                questionsDuSegment(base.html, segment),
                ancres
            ),
        }
    })
}

/**
 * Un renvoi vers une section absente pour ce segment (« les deux scénarios »
 * en C5) garde son texte et perd son lien : aucune ancre ne pointe dans le vide.
 */
export function liensVersSectionsRendues(
    html: string,
    ancres: ReadonlySet<string>
): string {
    return html.replace(
        /<a href="#([^"]*)">([\s\S]*?)<\/a>/g,
        (lien, cible: string, texte: string) =>
            ancres.has(cible) ? lien : texte
    )
}

/** Les sections ouvertes au repos (S0) : l'essentiel, et ce qui change jusqu'à la fin de vie de l'ancienne version (31/12/2026) incluse. */
export function sectionsOuvertesAuRepos(maintenant: Date): string[] {
    const jour = maintenant.toLocaleDateString("sv-SE", {
        timeZone: "Europe/Paris",
    })
    return jour <= FIN_DE_VIE_V1 ? ["essentiel", "en-bref"] : ["essentiel"]
}
